import { createElement, FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { isEmpty, isObject } from 'lodash';
import { createBemBlockBuilder, Truncate } from '@ebsco-ui/ebsco-ui';

import {
  AccessOptions,
  CoverImageContainer,
  DelimitedList,
  FeatureFlagDisplay,
  PublicationInfo,
  RecordsTools,
  RecordSummaryAvailability,
  SearchInstanceSubjectsDto,
  SearchOption,
  SummaryPlacement,
} from '@app/components';
import { useRecordSummaryContext, useSearchContext } from '@app/contexts';
import {
  useCoverArtFetch,
  useInstanceDetailsRoutePath,
  useQueryParams,
} from '@app/hooks';
import { formatArrayToString, generateRouteParams } from '@app/utils';
import { DefaultCoverImageTypes, Feature, Route } from '@app/constants';

import { RecordDataItem } from '../../RecordDataItem';
import { ParentField } from './ParentField';
import { Contributors } from '../../Contributors';

import '../RecordSummary.scss';

interface RecordSummaryCardProps {
  isModal?: boolean;
  isRTACShown?: boolean;
}

type PlacementBemBlocks = Record<
  SummaryPlacement,
  ReturnType<typeof createBemBlockBuilder>
>;

const cnBemRecordSummary = createBemBlockBuilder(['recordSummary']);
const cnBemModal = createBemBlockBuilder(['recordSummaryModal']);
const cnBemResults = createBemBlockBuilder(['recordSummaryResults']);
const cnBemDetails = createBemBlockBuilder(['recordSummaryInstanceDetails']);
const cnBemHoldsAndCheckouts = createBemBlockBuilder([
  'recordSummaryHoldsAndCheckouts',
]);

const placementBemBlocks: PlacementBemBlocks = {
  results: cnBemResults,
  courses: cnBemResults,
  instanceDetails: cnBemDetails,
  holdsAndCheckouts: cnBemHoldsAndCheckouts,
};

export const RecordSummaryCard: FC<RecordSummaryCardProps> = ({
  isModal = false,
  isRTACShown = true,
}) => {
  const {
    instance,
    recordNumber,
    itemStatusData,
    placement,
    testId,
    isInstanceDetails,
    isMobileHoldsAndCheckouts,
    isHoldsAndCheckouts,
    isMobileInstanceDetails,
    getItemStatusPanel,
  } = useRecordSummaryContext();
  const { data: coverArtSrc, isInitialLoading: isCoverArtLoading } =
    useCoverArtFetch(instance.isbns);
  const { getInstanceDetailsRoutePath } = useInstanceDetailsRoutePath();
  const { setInstanceIdScrollTo } = useSearchContext();

  const cnBemModalOrPlacement = useMemo(
    () => (isModal ? cnBemModal : placementBemBlocks[placement]),
    [isModal, placement]
  );

  const cnBemWithCommon = (className: string) =>
    cn(cnBemRecordSummary(className), cnBemModalOrPlacement(className));

  const maxRecordsToDisplay = 3;
  const { $t } = useIntl();

  const isNonCatalogItem = Boolean(itemStatusData?.isNonCatalogItem);
  const isVirtualItem =
    isHoldsAndCheckouts && Boolean(itemStatusData?.isVirtualItem);
  const sourceTypes = formatArrayToString(instance?.sourceTypes);

  const queryParams = useQueryParams();

  const getCardTitle = () => {
    const cardTitle =
      !isInstanceDetails && !isNonCatalogItem && !isVirtualItem ? (
        <Link
          data-link={`${instance.id}-link`}
          data-auto={`${instance.id}-link`}
          tabIndex={0}
          to={getInstanceDetailsRoutePath(instance.id)}
          onClick={() => setInstanceIdScrollTo(instance.id)}
        >
          {instance.title}
        </Link>
      ) : (
        instance.title
      );
    const modalTitle = (
      <h3 className={cn(cnBemModal('__contentWrapper-title'))}>
        <Truncate maxLines={2} content={instance.title} />
      </h3>
    );
    const titleLevel: Record<SummaryPlacement, number> = {
      results: 2,
      courses: 2,
      instanceDetails: 1,
      holdsAndCheckouts: 2,
    };

    return isModal
      ? modalTitle
      : createElement(
          `h${titleLevel[placement]}`,
          {
            className: cn(
              cnBemRecordSummary('__metadata-title'),
              cnBemModalOrPlacement('__contentWrapper-title')
            ),
            dir: 'ltr',
            'data-auto': 'record-summary-title',
          },
          cardTitle
        );
  };

  const isCoursesPlacement = placement === 'courses';
  const isInstanceDetailsPlacement = placement === 'instanceDetails';
  const isHoldsAndCheckoutsPlacement = placement === 'holdsAndCheckouts';
  const isValidPlacement = placement === 'results' || isCoursesPlacement;

  const getSubjects = () => {
    const areSubjectsHidden =
      isEmpty(instance.subjects) || !isValidPlacement || isModal;

    if (areSubjectsHidden) {
      return null;
    }

    return (
      <RecordDataItem
        label={$t({
          id: 'resultsList.subjects',
          defaultMessage: 'Subjects:',
        })}
        data-auto="subjects"
      >
        {instance.subjects && (
          <DelimitedList
            limit={maxRecordsToDisplay}
            delimiter=", "
            items={instance.subjects.map(
              (subject: SearchInstanceSubjectsDto[0], index: number) => {
                const value = isObject(subject) ? subject.value : subject;

                return (
                  <Link
                    key={index}
                    to={generateRouteParams({
                      pathname: Route.search,
                      query: {
                        ...queryParams,
                        pageNumber: 1,
                        option: SearchOption.subject,
                        query: value,
                      },
                    })}
                    className={cn(
                      'underlinedLink',
                      cnBemModalOrPlacement('__link')
                    )}
                  >
                    {value}
                  </Link>
                );
              }
            )}
          />
        )}
      </RecordDataItem>
    );
  };

  const isEditionPresent =
    !isEmpty(instance.editions) &&
    (isValidPlacement || isInstanceDetailsPlacement) &&
    !isModal;
  const isCoursesOrCheckoutsPlacement =
    isCoursesPlacement || isHoldsAndCheckoutsPlacement;

  return (
    <div
      data-auto={testId}
      className={cn(cnBemRecordSummary(), cnBemModalOrPlacement())}
    >
      {!isModal && recordNumber && (
        <p
          data-auto="record-number"
          className={cnBemRecordSummary('__recordNumber')}
        >
          {recordNumber}
        </p>
      )}
      <div className={cnBemWithCommon('__contentWrapper')}>
        {!isCoursesOrCheckoutsPlacement && (
          <CoverImageContainer
            src={coverArtSrc}
            sourceTypes={
              isNonCatalogItem ? DefaultCoverImageTypes.nonCatalog : sourceTypes
            }
            isLoading={isCoverArtLoading}
            imageClassName={cnBemWithCommon('__coverImage')}
            className={cnBemWithCommon('__defaultImageWrapper')}
          />
        )}
        <div
          className={cn(cnBemRecordSummary('__metadata'), {
            [cnBemRecordSummary('__metadata-courses')]:
              isCoursesOrCheckoutsPlacement,
          })}
        >
          {sourceTypes && (
            <div
              className={cn(
                cnBemModalOrPlacement('__type'),
                cnBemWithCommon('__metadata-type')
              )}
              data-auto="source-types"
            >
              {sourceTypes}
            </div>
          )}
          {getCardTitle()}
          <div className={cnBemRecordSummary('__recordSection')}>
            <Contributors
              contributors={instance.contributors}
              isModal={isModal}
              cnBemModalOrPlacement={cnBemModalOrPlacement}
              isVirtualItem={isVirtualItem}
            />
            <ParentField />
            <PublicationInfo publication={instance.publication} />
            {isEditionPresent && (
              <RecordDataItem
                label={$t({
                  id: 'resultsList.edition',
                  defaultMessage: 'Edition:',
                })}
                data-auto="edition"
              >
                {instance.editions?.join(', ')}
              </RecordDataItem>
            )}
            {getSubjects()}
          </div>
          {!isModal && isRTACShown && (
            <>
              <div className={cnBemRecordSummary('__recordSection')}>
                <RecordSummaryAvailability />
              </div>
              {!isMobileInstanceDetails && !isHoldsAndCheckouts && (
                <AccessOptions />
              )}
            </>
          )}
        </div>
      </div>
      {!isModal && (
        <FeatureFlagDisplay flagToMatch={{ name: Feature.LCDashboard }}>
          <div className={cnBemRecordSummary('__toolsWrapper')}>
            <RecordsTools entry={instance} />
          </div>
        </FeatureFlagDisplay>
      )}
      {isMobileInstanceDetails && !isModal && (
        <AccessOptions className={cnBemModalOrPlacement('__buttonsWrapper')} />
      )}
      {isMobileHoldsAndCheckouts &&
        itemStatusData &&
        !isModal &&
        getItemStatusPanel()}
    </div>
  );
};
